import { useEffect, useRef, useState } from "react";
import { apiClient } from "@/context/AuthContext";
import { Trash2, MapPin, DoorOpen, Flame, Droplets, Zap, AlertTriangle, Users, StepForward } from "lucide-react";

const PIN_META = {
  fire_cabinet: { label: "Yangın dolabı", color: "#ef4444", Icon: Flame },
  exit: { label: "Acil çıkış", color: "#22c55e", Icon: DoorOpen },
  hydrant: { label: "Hidrant", color: "#3b82f6", Icon: Droplets },
  sprinkler: { label: "Sprinkler", color: "#06b6d4", Icon: Droplets },
  detector: { label: "Dedektör", color: "#eab308", Icon: Zap },
  risk: { label: "Risk alanı", color: "#f97316", Icon: AlertTriangle },
  stair: { label: "Merdiven", color: "#a855f7", Icon: StepForward },
  assembly_point: { label: "Toplanma", color: "#10b981", Icon: Users },
  custom: { label: "Not", color: "#94a3b8", Icon: MapPin },
};

export default function PlanCanvas({ plan, onToast }) {
  const [img, setImg] = useState(null);
  const [pins, setPins] = useState([]);
  const [activeType, setActiveType] = useState("fire_cabinet");
  const [loading, setLoading] = useState(true);
  const stageRef = useRef(null);

  useEffect(() => {
    let aborted = false;
    (async () => {
      try {
        setLoading(true);
        const [imgRes, pinsRes] = await Promise.all([
          apiClient.get(`/plans/${plan.id}/image`),
          apiClient.get(`/plans/${plan.id}/pins`),
        ]);
        if (aborted) return;
        setImg(imgRes.data);
        setPins(pinsRes.data || []);
      } catch (err) {
        onToast?.(err?.response?.data?.detail || "Kroki yüklenemedi");
      } finally { setLoading(false); }
    })();
    return () => { aborted = true; };
  }, [plan.id, onToast]);

  const addPin = async (e) => {
    const rect = stageRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const y = Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height));
    try {
      const { data } = await apiClient.post(`/plans/${plan.id}/pins`, {
        type: activeType,
        label: PIN_META[activeType]?.label || activeType,
        x, y,
      });
      setPins((rows) => [...rows, data]);
    } catch (err) {
      onToast?.(err?.response?.data?.detail || "Pin eklenemedi");
    }
  };

  const removePin = async (id, ev) => {
    ev.stopPropagation();
    try {
      await apiClient.delete(`/plans/${plan.id}/pins/${id}`);
      setPins((rows) => rows.filter((p) => p.id !== id));
    } catch { onToast?.("Pin silinemedi"); }
  };

  const isImage = img?.content_type?.startsWith("image/");

  return (
    <div className="plan-canvas" data-testid="plan-canvas">
      <div className="pin-toolbar" data-testid="pin-toolbar">
        {Object.entries(PIN_META).map(([type, meta]) => {
          const Icon = meta.Icon;
          return (
            <button key={type} type="button"
                    className={`pin-tool ${activeType === type ? "active" : ""}`}
                    style={{"--pin-color": meta.color}}
                    onClick={() => setActiveType(type)}
                    data-testid={`pin-tool-${type}`}>
              <Icon size={14}/> {meta.label}
            </button>
          );
        })}
      </div>
      <div className="plan-stage" ref={stageRef} onClick={addPin} data-testid="plan-stage">
        {loading && <div className="plan-loading">Kroki yükleniyor…</div>}
        {!loading && isImage && (
          <img src={`data:${img.content_type};base64,${img.data_b64}`} alt={img.file_name} draggable={false}/>
        )}
        {!loading && !isImage && (
          <div className="plan-pdf-fallback">
            <small>PDF krokiler önizlenmez; pinleri ızgara üzerinde yerleştirebilirsiniz. Dosya: {img?.file_name}</small>
          </div>
        )}
        {pins.map((pin) => {
          const meta = PIN_META[pin.type] || PIN_META.custom;
          const Icon = meta.Icon;
          return (
            <div key={pin.id}
                 className="plan-pin-marker"
                 style={{left: `${pin.x*100}%`, top: `${pin.y*100}%`, "--pin-color": meta.color}}
                 title={pin.label}
                 data-testid={`pin-marker-${pin.id}`}
                 onClick={(e) => e.stopPropagation()}>
              <span className="pin-dot"><Icon size={11}/></span>
              <span className="pin-chip">{pin.label}<button type="button" onClick={(e) => removePin(pin.id, e)} data-testid={`pin-delete-${pin.id}`}><Trash2 size={10}/></button></span>
            </div>
          );
        })}
      </div>
      <small className="plan-canvas-foot">Krokide boş bir noktaya tıklayarak seçili tipte pin eklersiniz · {pins.length} işaret</small>
    </div>
  );
}
